import { UserCard } from "@/components/cards";
import { ModalLoadFailedContent } from "@/components/modals";
import { PartnerSendReportForm } from "@/forms/partner-send-report-form";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { useCitadel } from "@/hooks/use-citadel";
import { usePartnerTimeline } from "@/hooks/use-partner-timeline";
import { Alert } from "@msuite/ui/alert";
import { ProcessButton, useProcessButton } from "@msuite/ui/button";
import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { filterNonNull, guardArray } from "@msuite/utils/array";
import { getAllHourlyReferencesApproved } from "@msuite/utils/partnerships";
import { moment } from "@msuite/utils/date";
import { formatNumber, guardNumber } from "@msuite/utils/numbers";
import { partnerSendReportFormSchema } from "@msuite/utils/schemas";
import { getFullName } from "@msuite/utils/users";
import { motion } from "motion/react";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";

/** Props Interface */
interface PartnerTimelineInformationProps {
	currentWeek: string;
	partnershipId: string;
}

export function PartnerTimelineInformation({
	currentWeek,
	partnershipId,
}: PartnerTimelineInformationProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { organization } = useAuth();
	const { citadel } = useCitadel();

	/** Query */
	const { data, refetch, isLoading } = usePartnerTimeline({ partnershipId, currentWeek });

	const uncalculatedReferencesAsyncModal = useAsyncModal();
	const recipientFormAsyncModal = useAsyncModal<{ success: boolean; email: string }>();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		if (!organization?.id) throw new Error();
		const response = await client.organizations[":orgId"].partnerships[
			":partnershipId"
		].reports.recipients.$get({
			param: { orgId: organization?.id, partnershipId },
		});
		const data = await responseHandler(response);
		return {
			recipients: filterNonNull([data.recipient]),
			sendCopyToCreator: true,
		};
	});
	const methods = useForm({
		defaultValues,
		resolver: resolver(partnerSendReportFormSchema),
	});

	/** Computed */
	const allHourlyReferencesApproved = getAllHourlyReferencesApproved(
		guardArray(data?.timeline).flatMap((section) =>
			guardArray(section.items).map((i) => i.item.reference),
		),
	);

	const controller = useProcessButton({
		isPreparing: isLoading,
		initialState: guardArray(data?.invoiceTracking).length > 0 ? "completed" : "pending",
		isDisabled: !citadel?.can("update", "Partner") || !allHourlyReferencesApproved,
		labels: {
			pending: t("partners.send_report"),
			processing: t("partners.sending_report"),
			completed: t("partners.report_sent"),
			failed: t("partners.report_failed"),
		},
		onBeforeClick: async () => {
			await refetch();
			if (guardNumber(data?.calculatedTimeline.numberOfUncalculatedReferences)) {
				const isConfirmed = await uncalculatedReferencesAsyncModal.confirm();
				if (!isConfirmed) return false;
			}
			const isConfirmed = await recipientFormAsyncModal.confirm(methods);
			if (!isConfirmed) return false;
			return true;
		},
		onClick: async () => {
			if (!organization?.id) throw new Error("Organization ID is required");
			const json = methods.getValues();
			const response = await client.organizations[":orgId"].partnerships[
				":partnershipId"
			].timeline.report.$post({
				param: {
					orgId: organization?.id,
					partnershipId,
				},
				query: {
					week: currentWeek,
				},
				json,
			});
			const data = await responseHandler(response);
			await refetch();
			return data.result;
		},
	});

	/** Render */
	return (
		<React.Fragment>
			<motion.div
				key={`partner-timeline-information-${currentWeek}`}
				initial={{ opacity: 0, scale: 0.975 }}
				animate={{ opacity: 1, scale: 1 }}
				exit={{ opacity: 0, scale: 0.975 }}
				transition={{ duration: 0.15, ease: "circInOut" }}
				className="flex flex-col gap-4"
			>
				<ContentCard
					title={moment()
						.getMomentByDbWeek(currentWeek)
						.format(`[${t("dates.calendarweek")}] WW / GGGG`)}
					icon={icon("FileText")}
				>
					<DataGrid>
						<DataGrid.Row>
							<DataGrid.Label>{t("partners.hourly")}</DataGrid.Label>
							<DataGrid.Value>
								{guardNumber(data?.calculatedTimeline.numberOfHourlyReferences)}
							</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("partners.hourly_total")}</DataGrid.Label>
							<DataGrid.Value>
								{formatNumber(data?.calculatedTimeline.sumOfHourlyCalculations, "currency")}
							</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("partners.service")}</DataGrid.Label>
							<DataGrid.Value>
								{guardNumber(data?.calculatedTimeline.numberOfServiceReferences)}
							</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("partners.service_total")}</DataGrid.Label>
							<DataGrid.Value>
								{formatNumber(data?.calculatedTimeline.sumOfServiceCalculations, "currency")}
							</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Divider />
						<DataGrid.Row>
							<DataGrid.Label>{t("partners.uncaluclated")}</DataGrid.Label>
							<DataGrid.Value>
								{guardNumber(data?.calculatedTimeline.numberOfUncalculatedReferences)}
							</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Divider />
						<DataGrid.Row>
							<DataGrid.Label>{t("partners.total")}</DataGrid.Label>
							<DataGrid.Value>
								{formatNumber(data?.calculatedTimeline.totalSum, "currency")}
							</DataGrid.Value>
						</DataGrid.Row>
					</DataGrid>
				</ContentCard>

				{guardArray(data?.invoiceTracking).length > 0 && (
					<ContentCard
						title={t("partners.reports.last_sent")}
						icon={icon("Send")}
					>
						<DataGrid>
							<DataGrid.Row>
								<DataGrid.Label>{t("partners.reports.last_sent_by")}</DataGrid.Label>
								<UserCard
									withAvatar={false}
									variant="plain"
									userId={
										data?.invoiceTracking.at(0)?.updatedBy ||
										data?.invoiceTracking?.at(0)?.createdBy
									}
									render={(user) => <DataGrid.Value>{getFullName(user)}</DataGrid.Value>}
								/>
							</DataGrid.Row>
							<DataGrid.Row>
								<DataGrid.Label>{t("partners.reports.last_sent_at")}</DataGrid.Label>
								<DataGrid.Value>
									{moment(
										data?.invoiceTracking?.at(0)?.updatedAt ||
											data?.invoiceTracking?.at(0)?.createdAt,
									).longFormat()}
								</DataGrid.Value>
							</DataGrid.Row>
							<DataGrid.Row>
								<DataGrid.Label>{t("partners.reports.reports")}</DataGrid.Label>
								<DataGrid.Value>{guardArray(data?.invoiceTracking.length)}</DataGrid.Value>
							</DataGrid.Row>
						</DataGrid>
					</ContentCard>
				)}

				<div className="h-stack justify-center">
					<ProcessButton controller={controller} />
				</div>
			</motion.div>
			<FormProvider {...methods}>
				<ConfirmModal
					controller={recipientFormAsyncModal}
					message={null}
					title={t("partners.send_report")}
					confirmVariant="primary"
					confirmLabel={t("partners.send_report")}
					confirmIsDisabled={loadFailed}
				>
					{loadFailed ? <ModalLoadFailedContent /> : <PartnerSendReportForm />}
				</ConfirmModal>
				<ConfirmModal
					controller={uncalculatedReferencesAsyncModal}
					message={null}
					title={t("partners.send_report")}
				>
					<Alert
						variant="warning"
						title={t("general.caution")}
						description={t("partners.uncalculated_references_warning")}
						spacing="compact"
					/>
				</ConfirmModal>
			</FormProvider>
		</React.Fragment>
	);
}
